import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useUpdateEvaluationQuestion,
  useDeleteEvaluationQuestion,
  useQuestionsByForm,
  useSkillsList,
} from "../logic/useEvaluationQuestion";
import { message, Popconfirm, Tooltip, Select } from "antd";
import "./EditableQuestion.scss";

const EditableQuestion = ({ question, onUpdated, index }) => {
  const [editMode, setEditMode] = useState(false);
  const [options, setOptions] = useState(question.options || []);
  const [optionInput, setOptionInput] = useState("");
  const [selectedSkillId, setSelectedSkillId] = useState(
    question.skill_id || null
  );

  const [branching, setBranching] = useState(question.branching || {});
  const [enableBranching, setEnableBranching] = useState(
    !!question.branching && Object.keys(question.branching).length > 0
  );

  const formId = question.form?.id || question.formId || "";
  const { data: allQuestions = [] } = useQuestionsByForm(formId);

  const { data: skillsData, isLoading: skillsLoading } = useSkillsList();
  const skills = Array.isArray(skillsData) ? skillsData : [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      text: question.text,
      input_type: question.input_type,
      is_required: question.is_required,
    },
  });

  const inputType = watch("input_type");

  const { mutate: updateQuestion, isLoading } = useUpdateEvaluationQuestion();
  const { mutate: deleteQuestion } = useDeleteEvaluationQuestion();

  useEffect(() => {
    if (inputType === "scale") {
      setOptions(["1", "2", "3", "4"]);
    } else if (!["radio", "checkbox"].includes(inputType)) {
      setOptions([]);
      setSelectedSkillId(null);
      setBranching({});
      setEnableBranching(false);
    }
  }, [inputType]);

  const onSubmit = (data) => {
    const payload = {
      text: data.text,
      input_type: data.input_type,
      is_required: !!data.is_required,
    };

    if (data.input_type === "scale" && selectedSkillId) {
      payload.skill_id = selectedSkillId;
      payload.options = ["1", "2", "3", "4"];
    } else if (["radio", "checkbox"].includes(data.input_type)) {
      payload.skill_id = null;
      payload.options = options.map((o) => o.trim()).filter(Boolean);
      if (["radio", "checkbox"].includes(data.input_type)) {
        payload.branching = enableBranching
          ? branching
          : Object.keys(question.branching || {}).length > 0
          ? null
          : undefined;
      }
    } else {
      payload.skill_id = null;
      payload.options = [];
      payload.branching = undefined;
    }

    updateQuestion(
      { id: question.id, ...payload },
      {
        onSuccess: () => {
          message.success("Question updated successfully");
          setEditMode(false);
          onUpdated?.();
        },
        onError: () => message.error("Failed to update the question"),
      }
    );
  };

  const handleDelete = () => {
    deleteQuestion(question.id, {
      onSuccess: () => {
        message.success("Question deleted successfully");
        onUpdated?.();
      },
      onError: () => message.error("Failed to delete the question"),
    });
  };

  return editMode ? (
    <form onSubmit={handleSubmit(onSubmit)} className="editable-form">
      <label>Question</label>
      <input
        className="input-standard"
        {...register("text", { required: "Question text is required" })}
      />
      {errors.text && <p className="error">{errors.text.message}</p>}

      <label>Type</label>
      <select
        {...register("input_type")}
        className="input-standard"
        onChange={(e) => {
          const type = e.target.value;
          reset((prev) => ({ ...prev, input_type: type }));
          setEnableBranching(false);
          setBranching({});
          setOptions([]);
          if (type !== "scale") setSelectedSkillId(null);
        }}
      >
        <option value="text">Text</option>
        <option value="scale">Skill Level (1–4)</option>
        <option value="radio">Radio</option>
        <option value="checkbox">Checkbox</option>
      </select>

      {inputType === "scale" && (
        <>
          <label>Linked Skill</label>
          <Select
            showSearch
            placeholder="Select a skill"
            optionFilterProp="label"
            value={selectedSkillId}
            onChange={(value) => setSelectedSkillId(value)}
            loading={skillsLoading}
            className="input-standard"
          >
            {skills.map((skill) => (
              <Select.Option key={skill.id} value={skill.id}>
                {skill.name}
              </Select.Option>
            ))}
          </Select>
        </>
      )}

      {["radio", "checkbox"].includes(inputType) && (
        <>
          <label>Options</label>
          <div className="options-editor">
            <div>
              <input
                type="text"
                value={optionInput}
                onChange={(e) => setOptionInput(e.target.value)}
                placeholder="Add option"
                className="input-standard"
              />
              <button
                type="button"
                onClick={() => {
                  if (optionInput.trim()) {
                    setOptions([...options, optionInput.trim()]);
                    setOptionInput("");
                  }
                }}
              >
                +
              </button>
            </div>
          </div>

          <ul className="option-list">
            {options.map((opt, i) => (
              <li key={i} className="option-item">
                <div className="option-row">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[i] = e.target.value;
                      setOptions(updated);
                    }}
                    className="input-standard"
                  />
                  <span
                    className="remove-option"
                    onClick={() =>
                      setOptions(options.filter((_, idx) => idx !== i))
                    }
                    title="Remove option"
                  >
                    ×
                  </span>
                </div>

                {enableBranching && (
                  <Select
                    placeholder="Show question if selected"
                    value={branching[opt] || undefined}
                    onChange={(value) =>
                      setBranching((prev) => ({ ...prev, [opt]: value }))
                    }
                    style={{ width: "100%", marginTop: "0.5rem" }}
                  >
                    {Array.isArray(allQuestions) &&
                      allQuestions
                        .filter((q) => q.text && q.id !== question.id)
                        .map((q) => (
                          <Select.Option key={q.id} value={q.id}>
                            {q.text}
                          </Select.Option>
                        ))}
                  </Select>
                )}
              </li>
            ))}
          </ul>

          <div className="branching-toggle">
            {!enableBranching ? (
              <button
                type="button"
                onClick={() => setEnableBranching(true)}
                className="branching-button branching-add"
              >
                + Add branching logic
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setEnableBranching(false);
                  setBranching({});
                }}
                className="branching-button branching-cancel"
              >
                ✖ Cancel branching
              </button>
            )}
          </div>
        </>
      )}

      <label>
        <input type="checkbox" {...register("is_required")} /> Required
      </label>

      <div className="form-actions">
        <button
          type="button"
          className="cancel-button"
          onClick={() => setEditMode(false)}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="add-btn"
          disabled={
            isLoading ||
            (["radio", "checkbox"].includes(inputType) &&
              options.some((opt) => typeof opt !== "string" || !opt.trim()))
          }
        >
          Save
        </button>
      </div>
    </form>
  ) : (
    <div className="question-preview">
      <div className="question-actions">
        <button className="edit-toggle" onClick={() => setEditMode(true)}>
          Edit
        </button>
        <Popconfirm
          title="Are you sure you want to delete this question?"
          onConfirm={handleDelete}
          okText="Yes"
          cancelText="No"
        >
          <Tooltip title="Delete this question">
            <button className="delete-btn">Delete</button>
          </Tooltip>
        </Popconfirm>
      </div>

      <p className="question-text" data-index={index}>
        {question.text}
      </p>

      {question.input_type === "text" && (
        <input
          className="input-text"
          type="text"
          disabled
          placeholder="Answer here"
        />
      )}

      {question.input_type === "radio" && (
        <div className="input-radio">
          {question.options?.map((opt, i) => (
            <label key={i}>
              <input type="radio" disabled /> {opt}
            </label>
          ))}
        </div>
      )}

      {question.input_type === "checkbox" && (
        <div className="input-checkbox">
          {question.options?.map((opt, i) => (
            <label key={i}>
              <input type="checkbox" disabled /> {opt}
            </label>
          ))}
        </div>
      )}

      {question.input_type === "scale" && (
        <div className="input-scale">
          <div className="scale-buttons">
            {[1, 2, 3, 4].map((v) => (
              <button key={v} disabled>
                {v}
              </button>
            ))}
          </div>
          <div className="scale-labels">
            <span>Basic Level</span>
            <span>Expert Level</span>
          </div>
        </div>
      )}

      {!question.is_required && (
        <span className="optional-label">Optional</span>
      )}
    </div>
  );
};

export default EditableQuestion;
